// Toptan göçme katmanı: en kritik kayma dairesi (yay), merkez işareti, yarıçap çizgisi ve GK etiketi.
import { fmt, type GlobalStabilityResult, type Pt } from "@tasduvar/engine";
import { toSvg, type Transform } from "../geometry";

const INK = "rgb(var(--c-ink))", BAD = "rgb(var(--c-bad))";

/** Çıkış (ön) ve giriş (arka) noktaları arasındaki ALT yayın SVG yolu */
export function slipArcPath(t: Transform, gs: GlobalStabilityResult): string {
  const a = toSvg(t, gs.exit), b = toSvg(t, gs.entry);
  const R = gs.circle.r * t.scale;
  // model uzayında büyük yay bayrağı: merkezin altındaki yay her zaman küçük yaydır (< 180°) çünkü giriş/çıkış merkez kotunun altında
  return `M ${a.x.toFixed(1)} ${a.y.toFixed(1)} A ${R.toFixed(1)} ${R.toFixed(1)} 0 0 0 ${b.x.toFixed(1)} ${b.y.toFixed(1)}`;
}

/** Sahne kutusuna eklenecek noktalar: giriş, çıkış ve yayın en alt noktası */
export function slipExtraPoints(gs: GlobalStabilityResult | undefined): Pt[] {
  if (!gs) return [];
  const { cx, cy, r } = gs.circle;
  const xLow = Math.min(Math.max(cx, gs.exit.x), gs.entry.x);
  return [gs.exit, gs.entry, { x: xLow, y: cy - Math.sqrt(Math.max(0, r * r - (xLow - cx) ** 2)) }];
}

export function GlobalStability({ t, gs, label = true }: { t: Transform; gs: GlobalStabilityResult; label?: boolean }): JSX.Element {
  const color = gs.ok ? INK : BAD;
  const c = toSvg(t, { x: gs.circle.cx, y: gs.circle.cy });
  const ex = toSvg(t, gs.exit);
  const inside = c.x >= 0 && c.x <= t.width && c.y >= 0 && c.y <= t.height;
  const lab = toSvg(t, { x: (gs.exit.x + gs.entry.x) / 2, y: gs.circle.cy - gs.circle.r });
  return (
    <g data-part="global-stability" data-case={gs.caseId} data-ok={gs.ok}>
      <path data-part="slip-circle" d={slipArcPath(t, gs)} fill="none" stroke={color} strokeWidth={1.6} strokeDasharray="7 3" />
      <line data-part="slip-radius" x1={c.x} y1={c.y} x2={ex.x} y2={ex.y} stroke={color} strokeWidth={0.6} strokeDasharray="2 3" opacity={0.7} />
      {inside ? <g data-part="slip-center"><line x1={c.x - 5} y1={c.y} x2={c.x + 5} y2={c.y} stroke={color} strokeWidth={1} /><line x1={c.x} y1={c.y - 5} x2={c.x} y2={c.y + 5} stroke={color} strokeWidth={1} /><circle cx={c.x} cy={c.y} r={3} fill="none" stroke={color} strokeWidth={1} /></g> : null}
      {label ? <text x={lab.x} y={lab.y + 12} textAnchor="middle" className="text-[11px] num" fill={color}>GK {fmt(gs.fsMin, 2)} {gs.ok ? "≥" : "<"} {fmt(gs.target, 2)}{gs.caseId === "seismic" ? ` · kh ${fmt(gs.kh, 3)}` : ""}</text> : null}
    </g>
  );
}
